import React, { useState } from 'react';
import { Button, Stack, SxProps, TextField, Theme, Typography } from '@mui/material';
import { ReactComponent as StopIcon } from 'assets/icons/stop_icon.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check_icon.svg';
import { Dialog } from 'components/Dialog';
import { CampaignStatus } from 'types';
import { getFormattedChfCurrency } from 'utils';
import { useCampaign, useNotifications, useFeedback, useIsMobile } from 'hooks';

export const CampaignStop = ({
  status,
  invoicedAmount,
  campaignId,
  buttonStyles,
}: {
  status: CampaignStatus;
  invoicedAmount: number | null;
  campaignId: number;
  buttonStyles?: SxProps<Theme>;
}) => {
  const { setNotification } = useNotifications();
  const { isMobile } = useIsMobile();
  const { createFeedbackMutation } = useFeedback({
    onSuccess: () => {
      setNotification('Danke für dein wertvolles Feedback!', 'success');
    },
  });
  const [isConfirmModalOpened, setConfirmModalOpened] = useState(false);
  const [isFeedbackModalOpened, setFeedbackModalOpened] = useState(false);
  const [feedback, setFeedback] = useState('');
  const handleConfirmModalClose = () => {
    setConfirmModalOpened(false);
  };
  const { stopCampaignMutation, getCampaignQuery, getCampaignListQuery, getAllCampaignsQuery } = useCampaign();

  const handleFeedbackModalClose = () => {
    setFeedback('');
    setFeedbackModalOpened(false);

    getAllCampaignsQuery.refetch();
    getCampaignListQuery.refetch();
    getCampaignQuery.refetch();
  };

  const handleCampaignStop = () => {
    stopCampaignMutation.mutateAsync(campaignId as number).then(() => {
      handleConfirmModalClose();

      setFeedbackModalOpened(true);
    });
  };

  const handleSendFeedback = () => {
    createFeedbackMutation
      .mutateAsync({
        feedbackType: 'suggest',
        feedbackData: {
          comment: feedback,
        },
      })
      .then(handleFeedbackModalClose);
  };

  return (
    <>
      <Button sx={buttonStyles} startIcon={<StopIcon />} onClick={() => setConfirmModalOpened(true)}>
        Kampagne STOPPEN
      </Button>
      <Dialog
        size="sm"
        sx={{
          width: isMobile ? '400px !important' : '600px !important',
          margin: '0 auto',
        }}
        open={isConfirmModalOpened}
        title="Möchtest du die Kampagne wirklich stoppen?"
        content={
          status === CampaignStatus.ready ? (
            <Typography variant="body1">
              <b>Die Kampagne wird gestoppt und kann nicht neu gestartet werden.</b> Du kannst sie aber jederzeit
              duplizieren oder neu aufsetzen.
            </Typography>
          ) : (
            <>
              <Typography variant="body1" mb={2}>
                <b>Die Kampagne wird gestoppt und kann nicht wieder gestartet werden.</b> Du kannst sie aber jederzeit
                duplizieren und neu aufsetzen.
              </Typography>
              <Typography variant="body1" mb={2}>
                <b>Bisherige Kosten: {getFormattedChfCurrency(String(invoicedAmount ?? 0))}</b>
              </Typography>
              <Typography variant="body1" mb={2}>
                <b>Wichtiger Hinweis:</b> Nach dem Stopp kann es noch <b>bis zu 1 Stunde dauern</b>, bis alle Anzeigen
                von allen Werbeservern entfernt wurden. In dieser Zeit können einzelne Anzeigen noch angezeigt werden.
              </Typography>
              <Typography variant="body1">
                Daher können noch ein paar <b>zusätzliche Kosten entstehen.</b> Diese erscheinen auf deiner nächsten
                Rechnung.
              </Typography>
            </>
          )
        }
        primaryActionTitle="Kampagne stoppen"
        secondaryActionTitle="Nicht stoppen"
        onSecondaryActionClick={handleConfirmModalClose}
        onPrimaryActionClick={handleCampaignStop}
        isPrimaryActionLoading={stopCampaignMutation?.isPending}
      />
      <Dialog
        size="sm"
        onClose={handleFeedbackModalClose}
        sx={{
          width: isMobile ? '400px !important' : '600px !important',
          margin: '0 auto',
        }}
        open={isFeedbackModalOpened}
        title={
          <Stack direction="row" alignItems="center" alignContent="start">
            <CheckIcon />
            <Typography ml={2} variant="subtitle1" fontWeight={700} color="success.dark">
              Die Kampagne wurde erfolgreich gestoppt.
            </Typography>
          </Stack>
        }
        content={
          <>
            <Typography variant="body1" mb={6}>
              <b> Magst du uns kurz sagen, warum du sie gestoppt hast?</b>
              <br /> Dein Feedback hilft uns, Alloy Ads weiter zu verbessern.
            </Typography>
            <TextField
              value={feedback}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFeedback(e.target.value);
              }}
              label="Grund angeben (Optional)"
              placeholder="Gib hier den Grund an...."
              multiline
              minRows={4}
              maxRows={8}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  padding: '8px',
                  background: '#FFFFFF',
                },
                '& .MuiInputBase-input.MuiInputBase-inputMultiline': {
                  padding: '7px',
                },
              }}
            />
          </>
        }
        primaryActionTitle="FEEDBACK Senden"
        secondaryActionTitle="Schliessen"
        onSecondaryActionClick={handleFeedbackModalClose}
        onPrimaryActionClick={handleSendFeedback}
        isPrimaryActionLoading={createFeedbackMutation?.isPending}
      />
    </>
  );
};
